import { useTheme } from "expo-router/react-navigation";
import { Check, Copy } from "lucide-react-native";
import { ComponentRef, FC, ReactNode, useMemo, useRef, useState } from "react";
import { View } from "react-native";

import {
  Heatmap,
  HeatmapCell,
  HorizontalBarChart,
  HorizontalBarDataPoint,
} from "@/components/charts";
import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
import {
  copyChartToClipboard,
  copyElementAsImage,
} from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface RankingQuestionResultsProps {
  result: QuestionResults;
}

/* ── Copyable chart card ────────────────────────────────── */

interface ChartCardProps {
  children: ReactNode;
  /** Heatmaps are raster-copied via html2canvas; SVG charts use the
      shared clipboard helper. */
  imageCopy?: boolean;
  title: string;
}

/**
 * A single visualization with its own title and hover-to-copy button.
 * Ranking questions surface two complementary lenses (the average-rank
 * bar chart and the rank-distribution heatmap) so each gets its own card
 * and they sit side by side rather than behind a toggle.
 */
const ChartCard: FC<ChartCardProps> = ({ children, imageCopy, title }) => {
  const theme = useTheme();
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [copied, setCopied] = useState(false);

  const flash = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyVisual = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    const copy = imageCopy ? copyElementAsImage : copyChartToClipboard;
    await copy(element, flash);
  };

  return (
    <Card className="group flex-1 min-w-0 rounded-2xl border border-outline-200 p-6">
      <Box className="absolute z-10 top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <Pressable onPress={copyVisual}>
          <Box className="p-2 rounded-lg bg-background-100 dark:bg-background-800 hover:bg-background-200 dark:hover:bg-background-700 transition-colors">
            {copied ? (
              <Check color="rgb(var(--color-success-500))" size={16} />
            ) : (
              <Copy color={theme.colors.text} size={16} />
            )}
          </Box>
        </Pressable>
      </Box>
      <VStack className="gap-4">
        <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100 pr-8">
          {title}
        </Text>
        <View
          ref={chartRef}
          style={{
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {children}
        </View>
      </VStack>
    </Card>
  );
};

/* ── Component ──────────────────────────────────────────── */

const RankingQuestionResults: FC<RankingQuestionResultsProps> = ({
  result,
}) => {
  const { i18n } = useLocalization();
  const { groups, question } = result;

  const items = useMemo<string[]>(() => question.lines ?? [], [question.lines]);

  const numRanks = useMemo(
    () => question.options?.length ?? items.length,
    [question.options, items.length]
  );

  const totalRespondents = useMemo(
    () =>
      groups.reduce(
        (sum, g) =>
          sum +
          g.answers.filter(
            ({ answer }) => answer.type === "ranking" && answer.choices
          ).length,
        0
      ),
    [groups]
  );

  const rankingTitle = i18n.t("dashboardNavigator.chartTitleResponseRanking", {
    count: totalRespondents,
  });

  const heatmapTitle = i18n.t("dashboardNavigator.chartTitleResponseMatrix", {
    count: totalRespondents,
  });

  const barPoints = useMemo((): HorizontalBarDataPoint[] => {
    if (!items.length || numRanks < 2) return [];

    const scoreSums: number[] = items.map(() => 0);
    const scoreCounts: number[] = items.map(() => 0);

    groups.forEach((group) =>
      group.answers.forEach(({ answer }) => {
        if (answer.type === "ranking" && answer.choices) {
          answer.choices.forEach((rankIdx, itemIdx) => {
            if (itemIdx < items.length && rankIdx >= 0 && rankIdx < numRanks) {
              scoreSums[itemIdx] += numRanks - rankIdx;
              scoreCounts[itemIdx]++;
            }
          });
        }
      })
    );

    return items
      .map((label, idx) => {
        const avgScore =
          scoreCounts[idx] > 0 ? scoreSums[idx] / scoreCounts[idx] : 0;
        const avgRank = scoreCounts[idx] > 0 ? numRanks - avgScore + 1 : 0;
        const normalized = ((avgScore - 1) / (numRanks - 1)) * 100;
        return {
          count: scoreCounts[idx],
          label,
          tooltipLine: `${i18n.t("dashboardNavigator.avgRank")}: ${avgRank.toFixed(1)} · N=${scoreCounts[idx]}`,
          value: Math.round(normalized * 10) / 10,
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [groups, i18n, items, numRanks]);

  const columnLabels = useMemo(
    () => Array.from({ length: numRanks }, (_, i) => String(i + 1)),
    [numRanks]
  );

  const heatmapCells = useMemo((): HeatmapCell[] => {
    if (!items.length || numRanks < 2) return [];
    const counts: number[][] = items.map(() => columnLabels.map(() => 0));
    groups.forEach((group) =>
      group.answers.forEach(({ answer }) => {
        if (answer.type === "ranking" && answer.choices) {
          answer.choices.forEach((rankIdx, itemIdx) => {
            if (itemIdx < items.length && rankIdx >= 0 && rankIdx < numRanks) {
              counts[itemIdx][rankIdx]++;
            }
          });
        }
      })
    );
    return items.flatMap((_, rowIdx) =>
      columnLabels.map((_, colIdx) => ({
        column: colIdx,
        count: counts[rowIdx][colIdx],
        row: rowIdx,
      }))
    );
  }, [columnLabels, groups, items, numRanks]);

  if (!barPoints.length && !heatmapCells.length) {
    return null;
  }

  return (
    <HStack className="gap-4 flex-wrap items-start">
      {barPoints.length > 0 && (
        <ChartCard title={rankingTitle}>
          <HorizontalBarChart points={barPoints} />
        </ChartCard>
      )}
      {heatmapCells.length > 0 && (
        <ChartCard imageCopy title={heatmapTitle}>
          <Heatmap
            cells={heatmapCells}
            columnLabels={columnLabels}
            rowLabels={items}
            totalRespondents={totalRespondents}
          />
        </ChartCard>
      )}
    </HStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default RankingQuestionResults;
